import { Box, chakra, Flex, HStack, Separator, Stack, Text } from '@chakra-ui/react';
import { AlertTriangle, Clock3, ExternalLink, GitBranch, Newspaper } from 'lucide-react';

import { ExternalNewsPayloadSchema, type CatalystEvent, type ExternalNewsPayload } from '../api/types';
import { money, pp, relativeTime, spreadPct, timeLabel } from '../lib/format';
import { polymarketEventUrlFromEventSlug } from '../lib/polymarket';

interface CatalystEventsListProps {
  items: CatalystEvent[];
  leaderMarketID?: string;
  mode?: 'compact' | 'full';
}

export function CatalystEventsList({ items, leaderMarketID = '', mode = 'compact' }: CatalystEventsListProps) {
  return (
    <Stack
      separator={<Separator borderColor="border.subtle" opacity={0.7} />}
      gap={0}
      fontSize={mode === 'compact' ? 'sm' : 'md'}
    >
      {items.map((item) => (
        <CatalystEventRow key={item.id} item={item} leaderMarketID={leaderMarketID} mode={mode} />
      ))}
    </Stack>
  );
}

function CatalystEventRow({
  item,
  leaderMarketID,
  mode,
}: {
  item: CatalystEvent;
  leaderMarketID: string;
  mode: 'compact' | 'full';
}) {
  const tone = toneForSource(item.source);
  const Icon = tone.icon;
  const externalNews = item.source === 'external_news' ? externalNewsPayload(item) : null;
  const marketURL = item.polymarket_url || polymarketEventUrlFromEventSlug(item.event_slug);
  const sourceDetails = sourceDetailRows(item);
  const isLeader = leaderMarketID !== '' && item.market_id === leaderMarketID;

  return (
    <Box as="article" px={5} py={4} minH={mode === 'compact' ? '154px' : undefined}>
      <SourceRankStripe source={item.source} />
      <Flex
        direction={{ base: 'column', lg: 'row' }}
        align={{ lg: 'flex-start' }}
        justify={{ lg: 'space-between' }}
        gap={3}
      >
        <Box minW={0}>
          <HStack flexWrap="wrap" gap={2}>
            <HStack
              as="span"
              gap={1.5}
              borderRadius="oqControl"
              borderWidth="1px"
              borderColor={tone.borderColor}
              bg={tone.bg}
              color={tone.color}
              px={2.5}
              py={1}
              fontSize="xs"
              fontWeight="semibold"
            >
              <Icon size={14} />
              {sourceLabel(item.source)}
            </HStack>
            <Box
              as="span"
              borderRadius="oqControl"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="surface.control"
              px={2.5}
              py={1}
              fontSize="xs"
              fontWeight="semibold"
              color="text.muted"
            >
              {confidence(item.confidence)}
            </Box>
            {isLeader ? (
              <Box
                as="span"
                borderRadius="oqControl"
                borderWidth="1px"
                borderColor="brand.solid/20"
                bg="brand.subtle"
                px={2.5}
                py={1}
                fontSize="xs"
                fontWeight="semibold"
                color="brand.fg"
              >
                Featured
              </Box>
            ) : null}
            {tone.caution ? (
              <Box
                as="span"
                borderRadius="oqControl"
                borderWidth="1px"
                borderColor="signal.warn/25"
                bg="signal.warn/10"
                px={2.5}
                py={1}
                fontSize="xs"
                fontWeight="semibold"
                color="signal.warn"
              >
                Caution
              </Box>
            ) : null}
          </HStack>

          <Text mt={3} fontWeight="semibold" color="text.primary" lineClamp={mode === 'compact' ? 1 : undefined}>
            {item.title}
          </Text>
          {externalNews ? <ExternalNewsMeta payload={externalNews} /> : null}
          <Text mt={1} lineHeight="6" color="text.muted" lineClamp={mode === 'compact' ? 2 : undefined}>
            {item.summary}
          </Text>
          <Text mt={2} fontSize="sm" lineHeight="6" color="text.muted">
            {sourceExplanation(item.source)}
          </Text>

          <Box mt={3} borderRadius="oqControl" borderWidth="1px" borderColor="border.subtle" bg="surface.control" p={3}>
            <Flex flexWrap="wrap" align="flex-start" justify="space-between" gap={3}>
              <Box minW={0}>
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="text.muted">
                  Matched market
                </Text>
                <Text mt={1} fontWeight="medium" lineHeight="snug" color="text.primary" lineClamp={mode === 'compact' ? 2 : undefined}>
                  {item.question || `Market ${item.market_id}`}
                </Text>
                <HStack mt={1} flexWrap="wrap" gap={2} fontSize="xs" color="text.muted">
                  {item.category ? <Text as="span">{item.category}</Text> : null}
                  {item.quake_direction ? (
                    <Text as="span" maxW="8rem" truncate title={item.quake_direction}>
                      {item.quake_direction}
                    </Text>
                  ) : null}
                  {typeof item.quake_change_pp === 'number' ? <Text as="span">{pp(item.quake_change_pp)}</Text> : null}
                  {typeof item.quake_score === 'number' ? <Text as="span">score {Math.round(item.quake_score)}</Text> : null}
                </HStack>
              </Box>
              <Flex flexShrink={0} flexWrap="wrap" justify="flex-end" gap={2}>
                {marketURL ? (
                  <chakra.a
                    href={marketURL}
                    target="_blank"
                    rel="noreferrer noopener"
                    display="inline-flex"
                    alignItems="center"
                    gap={1.5}
                    borderRadius="oqControl"
                    borderWidth="1px"
                    borderColor="brand.solid/20"
                    bg="brand.subtle"
                    px={2.5}
                    py={1}
                    fontSize="xs"
                    fontWeight="semibold"
                    color="brand.fg"
                    transition="colors"
                    _hover={{ color: 'text.primary' }}
                  >
                    Polymarket <ExternalLink size={14} />
                  </chakra.a>
                ) : null}
                {externalNews?.url ? (
                  <chakra.a
                    href={externalNews.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    display="inline-flex"
                    alignItems="center"
                    gap={1.5}
                    borderRadius="oqControl"
                    borderWidth="1px"
                    borderColor="signal.good/25"
                    bg="signal.good/10"
                    px={2.5}
                    py={1}
                    fontSize="xs"
                    fontWeight="semibold"
                    color="signal.good"
                    transition="colors"
                    _hover={{ color: 'text.primary' }}
                  >
                    Article <ExternalLink size={14} />
                  </chakra.a>
                ) : null}
              </Flex>
            </Flex>
          </Box>

          {sourceDetails.length > 0 ? (
            <Flex mt={3} flexWrap="wrap" gap={2}>
              {sourceDetails.map((detail) => (
                <Box
                  as="span"
                  key={detail.label}
                  borderRadius="oqControl"
                  borderWidth="1px"
                  borderColor="border.subtle"
                  bg="surface.control"
                  px={2.5}
                  py={1}
                  fontSize="xs"
                  color="text.muted"
                >
                  <Text as="span" fontWeight="semibold" color="text.primary">
                    {detail.label}
                  </Text>{' '}
                  {detail.value}
                </Box>
              ))}
            </Flex>
          ) : null}
        </Box>

        <Text whiteSpace="nowrap" fontSize="sm" color="text.muted">
          {relativeTime(item.observed_at)}
        </Text>
      </Flex>
    </Box>
  );
}

// SourceRankStripe is a thin 1px line on the left edge whose tone
// mirrors the source ranking. News gets a full-bright stripe, related
// is medium, timing is dim, liquidity is faintest. This is the
// visible hierarchy — not just badge color — so a user scanning
// vertically can tell at a glance which row carries the strongest
// signal. Pure CSS, no extra DOM depth.
function SourceRankStripe({ source }: { source: string }) {
  const bg = sourceStripeBg(source);
  return <Box mb={3} h="0.5" w="full" borderRadius="full" opacity={0.9} bg={bg} aria-hidden />;
}

function sourceStripeBg(source: string) {
  if (source === 'external_news') return 'signal.good/70';
  if (source === 'related_market_cluster') return 'brand.solid/60';
  if (source === 'timing_boundary') return 'signal.warn/40';
  if (source === 'liquidity_warning') return 'text.muted/25';
  return 'text.muted/15';
}

function ExternalNewsMeta({ payload }: { payload: ExternalNewsPayload }) {
  const source = payload.source || payload.provider || 'External source';
  const published = payload.published_at ? timeLabel(payload.published_at) : '';
  return (
    <HStack mt={1} flexWrap="wrap" gap={2} fontSize="xs" color="text.muted">
      <Text as="span">{source}</Text>
      {published ? (
        <>
          <Text as="span" color="text.muted" opacity={0.5}>
            /
          </Text>
          <Text as="span">{published}</Text>
        </>
      ) : null}
    </HStack>
  );
}

function sourceLabel(source: string) {
  if (source === 'external_news') return 'External News';
  if (source === 'related_market_cluster') return 'Related Markets';
  if (source === 'timing_boundary') return 'Timing Boundary';
  if (source === 'liquidity_warning') return 'Liquidity Warning';
  return source;
}

function sourceExplanation(source: string) {
  if (source === 'external_news') return 'External article matched to this market by the backend enrichment worker.';
  if (source === 'related_market_cluster') return 'Several linked markets moved in the same direction.';
  if (source === 'timing_boundary') return 'This is a caution signal: the move may reflect expiry or resolution mechanics.';
  if (source === 'liquidity_warning') {
    return 'This is a caution signal: wide spread or low executed window volume can make the move noisy.';
  }
  return 'Backend catalyst signal attached to this market.';
}

function toneForSource(source: string): {
  icon: typeof AlertTriangle;
  borderColor: string;
  bg: string;
  color: string;
  caution: boolean;
} {
  if (source === 'external_news') {
    return {
      icon: Newspaper,
      borderColor: 'signal.good/30',
      bg: 'signal.good/10',
      color: 'signal.good',
      caution: false,
    };
  }
  if (source === 'related_market_cluster') {
    return {
      icon: GitBranch,
      borderColor: 'brand.solid/30',
      bg: 'brand.subtle',
      color: 'brand.fg',
      caution: false,
    };
  }
  if (source === 'timing_boundary') {
    return {
      icon: Clock3,
      borderColor: 'signal.warn/30',
      bg: 'signal.warn/10',
      color: 'signal.warn',
      caution: true,
    };
  }
  // Liquidity warning: downgraded to muted style. Wide spread and
  // thin books are not strong evidence the move is "real" — they are
  // a noisiness flag. Surfacing it in red over-claimed. Ranking and
  // color both reflect: news > related > timing > liquidity.
  return {
    icon: AlertTriangle,
    borderColor: 'border.subtle',
    bg: 'whiteAlpha.50',
    color: 'text.muted',
    caution: false,
  };
}

function sourceDetailRows(item: CatalystEvent) {
  const payload = recordPayload(item.payload);
  if (item.source === 'related_market_cluster') {
    return [
      detail('Related', numberPayload(payload, 'related_count')),
      detail('Agreement', pctPayload(payload, 'direction_agreement')),
      detail('Avg move', ppPayload(payload, 'avg_abs_move_pp')),
    ].filter((value): value is DetailRow => value !== null);
  }
  if (item.source === 'timing_boundary') {
    return [
      detail('End time', stringPayload(payload, 'end_time')),
      detail('Hours left', numberPayload(payload, 'hours_until_end')?.toFixed(1)),
    ].filter((value): value is DetailRow => value !== null);
  }
  if (item.source === 'liquidity_warning') {
    return [
      detail('Spread', spreadPayload(payload, 'spread')),
      detail('Window vol', moneyPayload(payload, 'window_volume')),
      detail('Depth', moneyPayload(payload, 'depth_near_mid')),
    ].filter((value): value is DetailRow => value !== null);
  }
  return [];
}

interface DetailRow {
  label: string;
  value: string;
}

function detail(label: string, value: string | number | undefined | null): DetailRow | null {
  if (value == null || value === '') return null;
  return { label, value: String(value) };
}

function externalNewsPayload(item: CatalystEvent) {
  const parsed = ExternalNewsPayloadSchema.safeParse(item.payload);
  return parsed.success ? parsed.data : null;
}

function recordPayload(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  return {};
}

function numberPayload(payload: Record<string, unknown>, key: string) {
  const value = payload[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function stringPayload(payload: Record<string, unknown>, key: string) {
  const value = payload[key];
  return typeof value === 'string' ? value : undefined;
}

function pctPayload(payload: Record<string, unknown>, key: string) {
  const value = numberPayload(payload, key);
  return value == null ? undefined : `${Math.round(value * 100)}%`;
}

function ppPayload(payload: Record<string, unknown>, key: string) {
  const value = numberPayload(payload, key);
  return value == null ? undefined : pp(value, false);
}

function spreadPayload(payload: Record<string, unknown>, key: string) {
  const value = numberPayload(payload, key);
  return value == null ? undefined : spreadPct(value);
}

function moneyPayload(payload: Record<string, unknown>, key: string) {
  const value = numberPayload(payload, key);
  return value == null ? undefined : money(value);
}

function confidence(value: number) {
  return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
}